(() => {
  if (customElements.get('fl-flavour-product')) return;

  class FLFlavourProduct extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.root = document.getElementById(`MainProduct-${this.sectionId}`);
      if (!this.root) return;

      this.entries = Array.from(this.querySelectorAll('[data-fl-bag]')).map((node) => ({
        variantId: String(node.dataset.variantId || ''),
        sku: node.dataset.sku || '',
        fit: node.dataset.fit || '',
        mediaId: String(node.dataset.mediaId || ''),
        src: node.dataset.src || '',
        alt: node.dataset.alt || '',
      })).filter((entry) => entry.variantId && entry.src);

      const mediaCounts = this.entries.reduce((counts, entry) => {
        if (entry.mediaId) counts.set(entry.mediaId, (counts.get(entry.mediaId) || 0) + 1);
        return counts;
      }, new Map());
      this.fixedEntries = this.entries.filter((entry) => entry.mediaId && mediaCounts.get(entry.mediaId) === 1);
      this.transientEntries = this.entries.filter((entry) => !entry.mediaId || mediaCounts.get(entry.mediaId) > 1);
      this.nativeImages = new WeakMap();
      this.transientImages = new Set();
      this.selectedVariantId = String(this.dataset.selectedVariantId || '');

      this.root.classList.add('fl-flavour-ready');
      this.root.style.setProperty('--fl-accent', this.dataset.accent || '#8BAF84');
      this.pageRoot = document.documentElement;
      this.pageRoot.classList.add('fl-flavour-page');
      this.pageRoot.dataset.flFlavour = this.dataset.flavourKey || '';
      this.pageRoot.style.setProperty('--fl-page-accent', this.dataset.accent || '#8BAF84');
      this.applyAll(this.selectedVariantId);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (String(event?.data?.sectionId || '') !== String(this.sectionId)) return;
          this.selectedVariantId = String(event?.data?.variant?.id || '');
          window.requestAnimationFrame(() => this.applyAll(this.selectedVariantId));
        });
      }
    }

    disconnectedCallback() {
      if (typeof this.unsubscribeVariant === 'function') this.unsubscribeVariant();
      if (this.pageRoot?.dataset.flFlavour === (this.dataset.flavourKey || '')) {
        this.pageRoot.classList.remove('fl-flavour-page');
        delete this.pageRoot.dataset.flFlavour;
        this.pageRoot.style.removeProperty('--fl-page-accent');
      }
    }

    applyAll(selectedVariantId) {
      this.fixedEntries.forEach((entry) => this.applyEntry(entry));
      this.restoreTransientImages();
      const selected = this.transientEntries.find((entry) => entry.variantId === String(selectedVariantId || ''));
      if (selected) this.applyEntry(selected, true);
      const fixed = this.fixedEntries.find((entry) => entry.variantId === String(selectedVariantId || ''));
      if (fixed) {
        const gallery = document.getElementById(`MediaGallery-${this.sectionId}`);
        gallery?.setActiveMedia?.(`${this.sectionId}-${fixed.mediaId}`);
      }
    }

    imageSelectors(entry) {
      if (!entry.mediaId) return [];
      const compoundId = `${this.sectionId}-${entry.mediaId}`;
      return [
        `#MediaGallery-${CSS.escape(this.sectionId)} [data-media-id="${CSS.escape(compoundId)}"] img`,
        `#Slider-Thumbnails-${CSS.escape(this.sectionId)} [data-target="${CSS.escape(compoundId)}"] img`,
        `#ProductModal-${CSS.escape(this.sectionId)} [data-media-id="${CSS.escape(compoundId)}"] img`,
        `#ProductModal-${CSS.escape(this.sectionId)} [data-media-id="${CSS.escape(entry.mediaId)}"] img`,
      ];
    }

    activeImages() {
      const gallery = document.getElementById(`MediaGallery-${this.sectionId}`);
      const activeMedia = gallery?.querySelector('.product__media-item.is-active[data-media-id]');
      const activeCompoundId = activeMedia?.dataset.mediaId || '';
      const selectors = [
        `#MediaGallery-${CSS.escape(this.sectionId)} .product__media-item.is-active img`,
      ];
      if (activeCompoundId) {
        selectors.push(
          `#Slider-Thumbnails-${CSS.escape(this.sectionId)} [data-target="${CSS.escape(activeCompoundId)}"] img`,
          `#ProductModal-${CSS.escape(this.sectionId)} [data-media-id="${CSS.escape(activeCompoundId)}"] img`,
          `#ProductModal-${CSS.escape(this.sectionId)} [data-media-id="${CSS.escape(activeCompoundId.split('-').pop() || '')}"] img`,
        );
      }
      return new Set(selectors.flatMap((selector) => Array.from(document.querySelectorAll(selector))));
    }

    rememberNative(image) {
      if (this.nativeImages.has(image)) return;
      this.nativeImages.set(image, {
        src: image.getAttribute('src'),
        srcset: image.getAttribute('srcset'),
        sizes: image.getAttribute('sizes'),
        alt: image.getAttribute('alt'),
      });
    }

    restoreTransientImages() {
      this.transientImages.forEach((image) => {
        const native = this.nativeImages.get(image);
        if (!native) return;
        for (const attribute of ['src', 'srcset', 'sizes', 'alt']) {
          const value = native[attribute];
          if (value == null) image.removeAttribute(attribute);
          else image.setAttribute(attribute, value);
        }
        delete image.dataset.flBagImage;
        delete image.dataset.flBagFit;
      });
      this.transientImages.clear();
    }

    applyEntry(entry, transient = false) {
      const selectors = this.imageSelectors(entry);
      const images = selectors.length
        ? new Set(selectors.flatMap((selector) => Array.from(document.querySelectorAll(selector))))
        : this.activeImages();
      images.forEach((image) => {
        this.rememberNative(image);
        if (transient) this.transientImages.add(image);
        image.src = entry.src;
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.alt = entry.alt;
        image.dataset.flBagImage = 'true';
        if (entry.fit) image.dataset.flBagFit = entry.fit;
        else delete image.dataset.flBagFit;
      });
    }
  }

  customElements.define('fl-flavour-product', FLFlavourProduct);
})();
